import store from '@/shared/store';
import { link } from '@/index/shared/filters';
import { ProductType } from '@/shared/enums';
import {
  getCategory,
  getDeviceParams,
  getDuration,
  getLocation,
  getProfileType,
  getServiceParams,
  getTechnicalParams,
  getTitle,
  getType,
  normalizeQuality,
  removeEmptyParams,
} from '@/shared/plugins/stats/helpers';
import { Emitter } from '@/shared/plugins/stats/emitter';
import ConsentMap from './consent-map';
import EventType from './event-type';

export default class GemiusStreamEmitter extends Emitter {
  constructor(stats, subscriber, oneTrust, liveManager) {
    super();
    this.stats = stats;
    this.oneTrust = oneTrust;
    this.liveManager = liveManager;
    this.subscriber = subscriber;
    this.subscriber.on('sync', this.updateUserParams, this);
    this.init();
  }

  updateUserParams() {
    if (this.subscriber.isLogged) {
      const profile = this.subscriber.profile;

      Object.assign(this.defaultParams, {
        param_user_profile_name: profile.name,
        param_user_profile_id: profile.id,
        param_user_profile_type: getProfileType(profile),
      });
    } else {
      delete this.defaultParams.param_user_profile_name;
      delete this.defaultParams.param_user_profile_id;
      delete this.defaultParams.param_user_profile_type;
    }
  }

  getVideoMetadata(product) {
    const { id, type, year } = product;
    const episode = type === ProductType.EPISODE ? product : product.episode;

    const params = {
      programName: getTitle(product) || 'No name',
      programDuration: getDuration(product),
      programType: 'video',
      param_id: id,
      param_title: getTitle(product),
      param_type: getType(product),
      param_series_title: episode?.season?.title,
      param_episode: episode?.episode,
      param_production_year: year,
      param_embed_site: getLocation().hostname,
      param_view_name: link(product).params.label,
      param_length: new Date(getDuration(product) * 1000).toISOString().substr(11, 8),
      param_category: getCategory(product),
    };

    return params;
  }

  init() {
    this.defaultParams = {
      ...getDeviceParams(),
      ...getServiceParams(),
    };
    this.updateUserParams();
  }

  setGemiusProgramPlayer({ product }) {
    const gemiusPlayer = new window.GemiusPlayer(product.id, window.pp_gemius_player_id);
    if (gemiusPlayer === store.state.gemiusPlayer) {
      return;
    }

    store.state.gemiusPlayer = new window.GemiusPlayer(product.id, window.pp_gemius_player_id);
    store.state.gemiusPlayer.newProgram(product.id, removeEmptyParams({
      ...this.getVideoMetadata(product),
    }));
  }

  setGemiusPlayerEvents({ product }) {
    store.state.gemiusPlayer.adEvent(product.id, 0, 'play');
    store.state.gemiusPlayer.adEvent(product.id, 0, 'pause');
    store.state.gemiusPlayer.adEvent(product.id, 0, 'stop');
    store.state.gemiusPlayer.adEvent(product.id, 0, 'complete');
    store.state.gemiusPlayer.adEvent(product.id, 0, 'close');
    store.state.gemiusPlayer.adEvent(product.id, 0, 'buffer');
    store.state.gemiusPlayer.adEvent(product.id, 0, 'seek');
    store.state.gemiusPlayer.adEvent(product.id, 0, 'next');
  }

  event(apiKey, { product, playerCurrentTime = 0, params = {} }) {
    if (!this.oneTrust.areConsentsAccepted(ConsentMap[apiKey])) {
      return;
    }

    const offsetTime = Math.min(playerCurrentTime, getDuration(product));

    store.state.gemiusPlayer?.programEvent(product.id, offsetTime, apiKey, removeEmptyParams({
      ...this.defaultParams,
      ...this.getVideoMetadata(product),
      ...getTechnicalParams(product, this.stats.bitrateMonitor),
      ...params,
    }));
  }

  startPlaying({ product }) {
    this.setGemiusProgramPlayer({ product });
    this.setGemiusPlayerEvents({ product });

    this.event(EventType.START_PLAYING, { product });
  }

  pausePlaying({ product, playerCurrentTime }) {
    this.event(EventType.PAUSE_PLAYING, {
      product,
      playerCurrentTime,
      params: {
        param_time_of_pause: playerCurrentTime,
      },
    });
  }

  closePlayer({ product, playerCurrentTime }) {
    this.event(EventType.CLOSE_PLAYER, {
      product,
      playerCurrentTime,
      params: {
        param_time_of_stop: playerCurrentTime,
      },
    });
  }

  bufferingWhilePlaying({ bufferingDuration, product, playerCurrentTime }) {
    this.event(EventType.BUFFERING_WHILE_PLAYING, {
      product,
      playerCurrentTime,
      params: {
        param_time_of_buffering: playerCurrentTime,
        param_buffering_duration: bufferingDuration,
      },
    });
  }

  changePlayingSettings({ quality, product }) {
    this.event(EventType.CHANGE_PLAYING_SETTINGS, {
      product,
      params: {
        param_stream_quality: normalizeQuality(quality),
      },
    });
  }

  complete({ product }) {
    this.event(EventType.COMPLETE, { product });
  }

  play({ product, playerCurrentTime }) {
    this.event(EventType.START_PLAYING, {
      product,
      playerCurrentTime,
      params: {
        param_time_of_play: playerCurrentTime,
      },
    });
  }

  playNext({ product }) {
    this.event(EventType.NEXT, { product });
  }

  seek({ product, playerCurrentTime }) {
    this.event(EventType.SEEK, {
      product,
      playerCurrentTime,
      params: {
        param_time_of_seeking: playerCurrentTime,
      },
    });
  }
}
